import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useT, useLocale, useDocumentTitleKey } from "@/i18n/locale-context";
import { useAuth } from "@/hooks/useAuth";
import { submitFeedback } from "@/lib/feedback.functions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Locale } from "@/i18n/types";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ & Suggestions — Glow Up Coach" },
      {
        name: "description",
        content:
          "Questions fréquentes sur la confidentialité, la sécurité, les abonnements et le fonctionnement de Glow Up Coach. Envoyez aussi vos suggestions ou signalez un problème.",
      },
    ],
  }),
  component: FaqPage,
});

type FaqItem = { q: string; a: string };
type FaqSection = { id: string; title: string; items: FaqItem[] };

const FAQ: Record<Locale, FaqSection[]> = {
  fr: [
    {
      id: "privacy",
      title: "Confidentialité & RGPD",
      items: [
        {
          q: "Mes photos sont-elles privées ?",
          a: "Oui. Vos selfies ne sont visibles que par vous. Par défaut, ils ne sont même pas stockés : seule l'analyse est conservée. Vous pouvez activer la conservation depuis Paramètres → Confidentialité, et la révoquer à tout moment — vos photos sont alors supprimées.",
        },
        {
          q: "Vendez-vous mes données ?",
          a: "Jamais. Nous ne vendons, ne louons et ne partageons aucune donnée personnelle avec des tiers à des fins commerciales. Glow Up Coach est financé par les abonnements, pas par la publicité.",
        },
        {
          q: "Où sont hébergées mes données ?",
          a: "L'infrastructure principale est hébergée dans l'Union européenne. Les traitements IA sont conçus pour respecter le RGPD et nous proposons une option « UE-only » dans les paramètres Pro.",
        },
        {
          q: "Comment supprimer mon compte et mes données ?",
          a: "Dans Paramètres → Compte, vous pouvez supprimer définitivement votre compte. Toutes vos analyses, selfies et données associées sont effacées sous 30 jours.",
        },
        {
          q: "Mes photos servent-elles à entraîner une IA ?",
          a: "Non. Vos selfies ne sont jamais utilisés pour entraîner des modèles d'IA — ni les nôtres ni ceux de nos prestataires.",
        },
      ],
    },
    {
      id: "security",
      title: "Sécurité",
      items: [
        {
          q: "Comment mes données sont-elles protégées ?",
          a: "Chiffrement en transit (HTTPS) et au repos, contrôle d'accès strict par Row-Level Security : seul votre compte authentifié peut lire vos données. Les selfies sont stockés via des URLs signées à durée limitée.",
        },
        {
          q: "Que se passe-t-il si je perds mon mot de passe ?",
          a: "Utilisez « Mot de passe oublié » sur la page de connexion. Vous recevrez un email de réinitialisation sécurisé.",
        },
        {
          q: "Et en cas d'incident de sécurité ?",
          a: "Nous suivons les obligations RGPD : notification aux utilisateurs concernés et à l'autorité compétente dans les délais légaux.",
        },
      ],
    },
    {
      id: "subscriptions",
      title: "Abonnements & paiements",
      items: [
        {
          q: "Que contient l'abonnement Premium ?",
          a: "Analyses illimitées, historique complet, rapports PDF, conseils détaillés et accès aux protocoles spécialisés Core. 4,99 €/mois ou 49 €/an (toutes taxes comprises).",
        },
        {
          q: "Et l'abonnement Pro ?",
          a: "Tout le Premium + protocoles spécialisés Pro, pré-check IA des photos (jusqu'à 30/jour), burst guidé et options de rétention avancées. 9,99 €/mois ou 89 €/an, avec 7 jours d'essai gratuit.",
        },
        {
          q: "Puis-je annuler à tout moment ?",
          a: "Oui. Depuis la page « Mon abonnement », vous pouvez annuler en un clic. Vous conservez l'accès jusqu'à la fin de la période déjà payée.",
        },
        {
          q: "Y a-t-il un remboursement ?",
          a: "Conformément au droit européen, vous bénéficiez d'un droit de rétractation de 14 jours si vous n'avez pas commencé à utiliser activement le service. Contactez-nous via le formulaire ci-dessous.",
        },
        {
          q: "Comment se passe le paiement ?",
          a: "Les paiements sont traités par Stripe (certifié PCI-DSS niveau 1). Nous ne voyons ni ne stockons vos coordonnées bancaires.",
        },
      ],
    },
    {
      id: "usage",
      title: "Utilisation",
      items: [
        {
          q: "Glow Up Coach pose-t-il un diagnostic médical ?",
          a: "Non. Glow Up Coach est un outil de coaching et de suivi visuel. Il ne pose aucun diagnostic et ne remplace pas l'avis d'un professionnel de santé. En cas de doute, consultez un médecin.",
        },
        {
          q: "À quelle fréquence dois-je prendre une photo ?",
          a: "Une photo par semaine suffit pour suivre vos progrès. Vous pouvez en faire plus si vous suivez un protocole spécifique.",
        },
        {
          q: "Pourquoi mon score varie-t-il peu d'une analyse à l'autre ?",
          a: "C'est volontaire : nous lissons les scores sur de courtes périodes pour éviter les variations dues à la lumière ou à l'angle. Les vrais progrès se voient sur plusieurs semaines.",
        },
      ],
    },
  ],
  en: [
    {
      id: "privacy",
      title: "Privacy & GDPR",
      items: [
        { q: "Are my photos private?", a: "Yes. Only you can see your selfies. By default they are not even stored — only the analysis is kept. You can enable retention in Settings → Privacy and revoke it anytime; your photos are then deleted." },
        { q: "Do you sell my data?", a: "Never. We never sell, rent or share personal data with third parties for commercial purposes. Glow Up Coach is funded by subscriptions, not ads." },
        { q: "Where is my data hosted?", a: "Our main infrastructure is hosted in the European Union. AI processing is designed to comply with GDPR and we offer an EU-only option in Pro settings." },
        { q: "How do I delete my account and data?", a: "In Settings → Account you can permanently delete your account. All analyses, selfies and related data are erased within 30 days." },
        { q: "Are my photos used to train AI?", a: "No. Your selfies are never used to train AI models — ours or our providers'." },
      ],
    },
    {
      id: "security",
      title: "Security",
      items: [
        { q: "How is my data protected?", a: "Encryption in transit (HTTPS) and at rest, strict Row-Level Security access control: only your authenticated account can read your data. Selfies are served through short-lived signed URLs." },
        { q: "What if I lose my password?", a: "Use \"Forgot password\" on the login page to receive a secure reset email." },
        { q: "What about security incidents?", a: "We follow GDPR obligations: timely notification to affected users and the relevant authority." },
      ],
    },
    {
      id: "subscriptions",
      title: "Subscriptions & billing",
      items: [
        { q: "What's included in Premium?", a: "Unlimited analyses, full history, PDF reports, detailed advice and access to Core specialized protocols. €4.99/month or €49/year (all taxes included)." },
        { q: "What about Pro?", a: "Everything in Premium + Pro specialized protocols, AI photo pre-check (up to 30/day), guided burst and advanced retention options. €9.99/month or €89/year, with a 7-day free trial." },
        { q: "Can I cancel anytime?", a: "Yes. From the \"My subscription\" page you can cancel in one click. Access continues until the end of the paid period." },
        { q: "Do you offer refunds?", a: "Under EU law you have a 14-day right of withdrawal if you have not actively started using the service. Contact us via the form below." },
        { q: "How are payments handled?", a: "Payments are processed by Stripe (PCI-DSS Level 1 certified). We never see or store your card details." },
      ],
    },
    {
      id: "usage",
      title: "Usage",
      items: [
        { q: "Does Glow Up Coach provide medical diagnosis?", a: "No. Glow Up Coach is a coaching and visual tracking tool. It does not diagnose and does not replace medical advice. If in doubt, consult a healthcare professional." },
        { q: "How often should I take a photo?", a: "One photo a week is enough to track progress. You can take more if you follow a specific protocol." },
        { q: "Why does my score change so little between analyses?", a: "By design: we smooth scores over short periods to avoid noise from lighting or angles. Real progress shows over several weeks." },
      ],
    },
  ],
  nl: [
    {
      id: "privacy",
      title: "Privacy & AVG",
      items: [
        { q: "Zijn mijn foto's privé?", a: "Ja. Alleen jij ziet je selfies. Standaard worden ze niet eens opgeslagen — alleen de analyse wordt bewaard. Je kunt bewaring inschakelen via Instellingen → Privacy en altijd intrekken; je foto's worden dan verwijderd." },
        { q: "Verkopen jullie mijn gegevens?", a: "Nooit. We verkopen, verhuren of delen geen persoonsgegevens met derden voor commerciële doeleinden. Glow Up Coach wordt gefinancierd door abonnementen, niet door advertenties." },
        { q: "Waar worden mijn gegevens gehost?", a: "Onze hoofdinfrastructuur staat in de Europese Unie. AI-verwerking is AVG-conform en in Pro-instellingen bieden we een EU-only optie." },
        { q: "Hoe verwijder ik mijn account?", a: "In Instellingen → Account verwijder je je account permanent. Alle analyses, selfies en gerelateerde data worden binnen 30 dagen gewist." },
        { q: "Worden mijn foto's gebruikt om AI te trainen?", a: "Nee. Je selfies worden nooit gebruikt om AI-modellen te trainen — niet die van ons, niet die van onze leveranciers." },
      ],
    },
    {
      id: "security",
      title: "Beveiliging",
      items: [
        { q: "Hoe worden mijn gegevens beschermd?", a: "Versleuteling in transit (HTTPS) en at rest, strikte Row-Level Security: alleen jouw geauthenticeerde account leest jouw data. Selfies worden geleverd via kortlopende signed URLs." },
        { q: "En als ik mijn wachtwoord vergeet?", a: "Gebruik \"Wachtwoord vergeten\" op de inlogpagina om een veilige reset-mail te ontvangen." },
        { q: "En bij een beveiligingsincident?", a: "We volgen de AVG-verplichtingen: tijdige melding aan betrokken gebruikers en de relevante autoriteit." },
      ],
    },
    {
      id: "subscriptions",
      title: "Abonnementen & betalingen",
      items: [
        { q: "Wat zit er in Premium?", a: "Onbeperkte analyses, volledige geschiedenis, PDF-rapporten, gedetailleerd advies en toegang tot Core gespecialiseerde protocollen. €4,99/maand of €49/jaar (incl. btw)." },
        { q: "En Pro?", a: "Alles uit Premium + Pro gespecialiseerde protocollen, AI-foto pre-check (tot 30/dag), geleide burst en geavanceerde bewaaropties. €9,99/maand of €89/jaar, met 7 dagen gratis proefperiode." },
        { q: "Kan ik altijd opzeggen?", a: "Ja. Via \"Mijn abonnement\" zeg je in één klik op. Toegang loopt door tot het einde van de betaalde periode." },
        { q: "Bieden jullie terugbetalingen?", a: "Volgens de EU-wetgeving heb je 14 dagen herroepingsrecht als je de dienst niet actief bent gaan gebruiken. Neem contact op via het formulier hieronder." },
        { q: "Hoe verlopen betalingen?", a: "Betalingen verlopen via Stripe (PCI-DSS Level 1 gecertificeerd). Wij zien noch bewaren je kaartgegevens." },
      ],
    },
    {
      id: "usage",
      title: "Gebruik",
      items: [
        { q: "Stelt Glow Up Coach een medische diagnose?", a: "Nee. Glow Up Coach is een coaching- en visuele opvolgtool. Het stelt geen diagnose en vervangt geen medisch advies. Bij twijfel, raadpleeg een arts." },
        { q: "Hoe vaak moet ik een foto nemen?", a: "Eén foto per week is genoeg om vooruitgang te volgen. Meer mag, zeker bij een specifiek protocol." },
        { q: "Waarom verandert mijn score nauwelijks tussen analyses?", a: "Bewust: we smoothen scores over korte periodes om ruis door licht of hoek te vermijden. Echte vooruitgang zie je over meerdere weken." },
      ],
    },
  ],
};

const LABELS: Record<Locale, Record<string, string>> = {
  fr: {
    kicker: "Centre d'aide",
    title: "FAQ & Suggestions",
    subtitle: "Tout ce que vous voulez savoir sur vos données, votre abonnement et le fonctionnement — et un espace pour nous écrire.",
    feedback_title: "Une suggestion ou un problème ?",
    feedback_subtitle: "Aidez-nous à améliorer Glow Up Coach. Nous lisons chaque message.",
    category: "Catégorie",
    cat_suggestion: "💡 Suggestion",
    cat_bug: "🐞 Bug / Problème",
    cat_question: "❓ Question",
    cat_other: "✉️ Autre",
    message: "Votre message",
    message_ph: "Décrivez aussi précisément que possible…",
    email: "Email (optionnel, pour la réponse)",
    email_ph: "vous@exemple.com",
    consent: "En envoyant ce formulaire, vous acceptez que nous traitions votre message pour vous répondre et améliorer le service.",
    send: "Envoyer",
    sending: "Envoi…",
    sent_toast: "Merci ! Votre message a bien été envoyé.",
    error_toast: "Impossible d'envoyer pour le moment. Réessayez.",
    no_question: "Vous ne trouvez pas votre réponse ?",
    contact_below: "Posez votre question via le formulaire ci-dessous.",
  },
  en: {
    kicker: "Help center",
    title: "FAQ & Suggestions",
    subtitle: "Everything you want to know about your data, subscription and how it works — and a place to write to us.",
    feedback_title: "Got a suggestion or an issue?",
    feedback_subtitle: "Help us improve Glow Up Coach. We read every message.",
    category: "Category",
    cat_suggestion: "💡 Suggestion",
    cat_bug: "🐞 Bug / Issue",
    cat_question: "❓ Question",
    cat_other: "✉️ Other",
    message: "Your message",
    message_ph: "Describe it as precisely as you can…",
    email: "Email (optional, so we can reply)",
    email_ph: "you@example.com",
    consent: "By submitting, you agree that we process your message to reply and improve the service.",
    send: "Send",
    sending: "Sending…",
    sent_toast: "Thanks! Your message has been sent.",
    error_toast: "Couldn't send right now. Please retry.",
    no_question: "Can't find your answer?",
    contact_below: "Ask via the form below.",
  },
  nl: {
    kicker: "Helpcentrum",
    title: "FAQ & Suggesties",
    subtitle: "Alles wat je wilt weten over je gegevens, abonnement en werking — en een plek om ons te schrijven.",
    feedback_title: "Een suggestie of probleem?",
    feedback_subtitle: "Help ons Glow Up Coach te verbeteren. We lezen elk bericht.",
    category: "Categorie",
    cat_suggestion: "💡 Suggestie",
    cat_bug: "🐞 Bug / Probleem",
    cat_question: "❓ Vraag",
    cat_other: "✉️ Andere",
    message: "Je bericht",
    message_ph: "Beschrijf het zo precies mogelijk…",
    email: "E-mail (optioneel, voor antwoord)",
    email_ph: "jij@voorbeeld.com",
    consent: "Door te verzenden ga je akkoord dat we je bericht verwerken om te antwoorden en de dienst te verbeteren.",
    send: "Verzenden",
    sending: "Bezig…",
    sent_toast: "Bedankt! Je bericht is verzonden.",
    error_toast: "Kon nu niet versturen. Probeer opnieuw.",
    no_question: "Vind je je antwoord niet?",
    contact_below: "Stel je vraag via het formulier hieronder.",
  },
};

function FaqPage() {
  useDocumentTitleKey("faq.head.title");
  const t = useT();
  void t;
  const { locale } = useLocale();
  const { user } = useAuth();
  const L = LABELS[locale] ?? LABELS.fr;
  const sections = FAQ[locale] ?? FAQ.fr;

  const send = useServerFn(submitFeedback);
  const [category, setCategory] = useState<"suggestion" | "bug" | "question" | "other">("suggestion");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (message.trim().length < 5) return;
    setLoading(true);
    try {
      const res = await send({
        data: {
          category,
          message: message.trim(),
          email: email.trim() || undefined,
          page_url: typeof window !== "undefined" ? window.location.href : undefined,
          locale,
          user_id: user?.id,
        },
      });
      if (!res.ok) throw new Error(res.error);
      toast.success(L.sent_toast);
      setDone(true);
      setMessage("");
      setEmail("");
    } catch {
      toast.error(L.error_toast);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{L.kicker}</p>
          <h1
            className="font-display font-bold text-4xl md:text-5xl tracking-tight mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {L.title}
          </h1>
          <p className="text-muted-foreground">{L.subtitle}</p>
        </div>

        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.id} id={section.id}>
              <h2 className="font-display font-semibold text-xl mb-3 tracking-tight">{section.title}</h2>
              <Accordion type="single" collapsible className="border border-foreground/10 rounded-2xl divide-y divide-foreground/10 bg-card">
                {section.items.map((item, i) => (
                  <AccordionItem key={i} value={`${section.id}-${i}`} className="border-0 px-4">
                    <AccordionTrigger className="text-left text-sm font-medium hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>

        <div className="mt-16 p-6 md:p-8 rounded-3xl bg-accent/10 border border-accent/20" id="feedback">
          <p className="text-xs uppercase tracking-widest text-accent mb-2">{L.no_question}</p>
          <h2
            className="font-display font-bold text-2xl md:text-3xl mb-2 tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {L.feedback_title}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">{L.feedback_subtitle}</p>

          {done ? (
            <div className="text-sm">
              ✓ {L.sent_toast}{" "}
              <button
                type="button"
                className="underline text-muted-foreground hover:text-foreground"
                onClick={() => setDone(false)}
              >
                +
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">{L.category}</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(["suggestion", "bug", "question", "other"] as const).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCategory(c)}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                        category === c
                          ? "border-accent bg-accent text-accent-foreground"
                          : "border-foreground/10 bg-background hover:border-foreground/20"
                      }`}
                    >
                      {L[`cat_${c}`]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">{L.message}</label>
                <textarea
                  required
                  minLength={5}
                  maxLength={5000}
                  rows={5}
                  placeholder={L.message_ph}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-foreground/15 bg-background text-sm focus:outline-none focus:border-accent resize-y"
                />
                <div className="text-[10px] text-muted-foreground mt-1 text-right">{message.length}/5000</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">{L.email}</label>
                <input
                  type="email"
                  maxLength={255}
                  placeholder={L.email_ph}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-foreground/15 bg-background text-sm focus:outline-none focus:border-accent"
                />
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{L.consent}</p>
              <button
                type="submit"
                disabled={loading || message.trim().length < 5}
                className="w-full md:w-auto bg-foreground text-background px-6 py-3 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {loading ? L.sending : L.send}
              </button>
            </form>
          )}
        </div>

        <div className="mt-12 text-center">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
            ← {locale === "fr" ? "Retour à l'accueil" : locale === "nl" ? "Terug naar home" : "Back to home"}
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}